import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert,
  CircularProgress, Divider, Tabs, Tab, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup,
  Grid
} from '@mui/material';
import {
  Edit, Map, Payment, History, Add,
  Close, MoreVert
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useApp } from '../context/AppContext';
import api from '../api/authApi';

// ── Info row ──────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.9, borderBottom: '1px solid #f0f0f0' }}>
      <Typography sx={{ flex: 1, fontSize: 13.5, color: '#555' }}>{label}</Typography>
      <Typography sx={{ flex: 1.2, fontSize: 13.5, color: '#222', fontWeight: 500, textAlign: 'right' }}>{value || ''}</Typography>
    </Box>
  );
}

// ── Setting row ───────────────────────────────────────────────
function SettingRow({ label, value, onEdit }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      py: 1.1, borderBottom: '1px solid #f5f5f5',
      '&:last-child': { borderBottom: 'none' }
    }}>
      <Typography sx={{ width: 200, fontSize: 13.5, color: '#333', fontWeight: 500, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ flex: 1, fontSize: 13.5, color: '#555' }}>
        {value || '—'}
      </Typography>
      <IconButton size="small" onClick={onEdit} sx={{ color: '#bbb', p: 0.3, ml: 0.5 }}>
        <MoreVert sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

// ── Section card ──────────────────────────────────────────────
function SectionCard({ title, actions, children, themeColor = '#5c9ea0' }) {
  return (
    <Card sx={{ mb: 2, borderRadius: 1.5, boxShadow: '0 1px 4px #0001', border: '1px solid #e8e8e8' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.2, borderBottom: '1px solid #eee'
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: themeColor }}>{title}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>{actions}</Box>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
    </Card>
  );
}

function EditBtn({ onClick, label = 'Edit', themeColor }) {
  return (
    <Button size="small" startIcon={<Edit sx={{ fontSize: 14 }} />} onClick={onClick}
      sx={{ fontSize: 13, color: themeColor || '#5c9ea0', textTransform: 'none', minWidth: 'auto', px: 0.8, py: 0.3 }}>
      {label}
    </Button>
  );
}

// ── On/Off dialog ─────────────────────────────────────────────
function OnOffDialog({ open, title, value, hint, onClose, onSave, themeColor = '#5c9ea0' }) {
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value, open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ background: themeColor, color: '#fff', fontSize: 14, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <RadioGroup value={val} onChange={e => setVal(e.target.value)}>
          <FormControlLabel value="On" control={<Radio size="small" />} label="On" />
          <FormControlLabel value="Off" control={<Radio size="small" />} label="Off" />
        </RadioGroup>
        {hint && <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>{hint}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
        <Button onClick={() => onSave(val)} variant="contained" size="small"
          sx={{ background: themeColor, '&:hover': { background: themeColor } }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════
export default function CenterInfo() {
  const { theme } = useApp();
  const themeColor = theme?.main || '#5c9ea0';

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [settingsTab, setSettingsTab] = useState(0);

  const [dlg, setDlg] = useState({
    center: false, billing: false, location: false, payment: false, history: false, addUser: false,
    collType: false, collShift: false, collInput: false, morningTime: false, eveningTime: false, collPrint: false,
    smsType: false, sendSmsFor: false, farmerAppLink: false, totalCollSms: false, centerNameSms: false,
    payPeriod: false, invPrint: false, payRegPrint: false,
    rateChart: false, shiftWiseRate: false, farmerWiseRate: false,
    showAdvLoan: false, showRateChart: false, showCollAfterInv: false, showCollShift: false, hideCollRate: false,
    billPeriod: false, milkSalePrint: false,
    bankDetails: false, clrFormat: false, fatFormat: false, computerLogin: false,
    annualBonus: false, weighScale: false, contactDetails: false, snfFormat: false, centerLogo: false,
  });

  const [centerForm, setCenterForm] = useState({});
  const [billingForm, setBillingForm] = useState({});
  const [locationForm, setLocationForm] = useState({});
  const [userForm, setUserForm] = useState({ name: '', mobile: '', role: 'Operator' });
  const [logoFile, setLogoFile] = useState(null);

  const settingAliases = {
    paymentPeriod: ['payPeriod'],
    billPeriod: ['billingPeriod'],
    collPrintConfig: ['collectionPrint'],
    invPrintConfig: ['invoicePrint'],
    payRegConfig: ['payRegPrint'],
    milkSaleConfig: ['milkSalePrint'],
    annualBonusConfig: ['annualBonus'],
  };
  const normalizeKey = (k) => String(k || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const openDlg = k => setDlg(d => ({ ...d, [k]: true }));
  const closeDlg = k => setDlg(d => ({ ...d, [k]: false }));
  const closeAll = () => setDlg(d => Object.fromEntries(Object.keys(d).map(k => [k, false])));

  const parseSettingList = (value, defaults = []) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const t = value.trim();
      if (!t) return defaults;
      return t.split(',').map(v => v.trim()).filter(Boolean);
    }
    return defaults;
  };

  const toggleListSetting = (key, option, defaults = []) => {
    setSettings(prev => {
      const current = new Set(parseSettingList(prev[key], defaults));
      if (current.has(option)) current.delete(option);
      else current.add(option);
      return { ...prev, [key]: Array.from(current).join(', ') };
    });
  };

  const defaultShiftDraft = {
    startHour: '01', startMinute: '00', startMeridiem: 'AM',
    endHour: '01', endMinute: '00', endMeridiem: 'PM'
  };

  const parseShiftString = (value) => {
    if (typeof value !== 'string') return { ...defaultShiftDraft };
    const m = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!m) return { ...defaultShiftDraft };
    return {
      startHour: String(m[1]).padStart(2, '0'),
      startMinute: String(m[2]).padStart(2, '0'),
      startMeridiem: m[3].toUpperCase(),
      endHour: String(m[4]).padStart(2, '0'),
      endMinute: String(m[5]).padStart(2, '0'),
      endMeridiem: m[6].toUpperCase(),
    };
  };

  const getShiftDraft = (dlgKey, valueKey) => settings[`${dlgKey}Draft`] || parseShiftString(settings[valueKey]);
  const setShiftDraft = (dlgKey, updater) => {
    setSettings(prev => {
      const baseKey = dlgKey === 'morningTime' ? 'morningShiftTime' : 'eveningShiftTime';
      const current = prev[`${dlgKey}Draft`] || parseShiftString(prev[baseKey]);
      return { ...prev, [`${dlgKey}Draft`]: updater(current) };
    });
  };

  const getSettingValue = (key, fallback = '') => {
    const keys = [key, ...(settingAliases[key] || [])];
    for (const k of keys) {
      const v = settings[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    const wanted = new Set(keys.map(normalizeKey));
    for (const [k, v] of Object.entries(settings || {})) {
      if (wanted.has(normalizeKey(k)) && v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
  };

  const getSettingDisplayValue = (key, fallback = '') => {
    const v = getSettingValue(key, fallback);
    if (v && typeof v === 'object') return 'Configured';
    return v || fallback || '';
  };

  const showMsg = (msg, isErr = false) => {
    if (isErr) setError(msg); else setSuccess(msg);
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const isApiSuccess = (resData) => {
    if (!resData) return false;
    if (resData.success !== undefined) return Boolean(resData.success);
    if (resData.Success !== undefined) return Boolean(resData.Success);
    // Some APIs return 200 with only data payload and no success flag.
    return true;
  };

  const extractPayload = (resData) => {
    if (!resData || typeof resData !== 'object') return null;
    if (resData.data !== undefined && resData.data !== null) return resData.data;
    if (resData.Data !== undefined && resData.Data !== null) return resData.Data;
    if (resData.result !== undefined && resData.result !== null) return resData.result;
    if (resData.Result !== undefined && resData.Result !== null) return resData.Result;
    return resData;
  };

  const parseSettingsMap = (resData) => {
    const out = {};
    const payload = extractPayload(resData);

    if (Array.isArray(payload)) {
      payload.forEach(item => {
        if (!item || typeof item !== 'object') return;

        let k = item?.settingKey ?? item?.SettingKey ?? item?.key ?? item?.Key;
        let v = item?.settingValue ?? item?.SettingValue ?? item?.value ?? item?.Value;

        if (!k) {
          const entries = Object.entries(item);
          const keyEntry = entries.find(([name]) => {
            const n = normalizeKey(name);
            return n.includes('key') || n === 'name' || n === 'setting';
          });
          const valEntry = entries.find(([name]) => {
            const n = normalizeKey(name);
            return n.includes('value') || n === 'val';
          });
          if (keyEntry) k = keyEntry[1];
          if (valEntry) v = valEntry[1];
        }

        if (!k && Object.keys(item).length === 1) {
          const onlyKey = Object.keys(item)[0];
          k = onlyKey;
          v = item[onlyKey];
        }

        if (k !== undefined && k !== null && String(k).trim() !== '') out[String(k)] = v;
      });
      return out;
    }

    if (payload && typeof payload === 'object') {
      const singleK = payload?.settingKey ?? payload?.SettingKey ?? payload?.key ?? payload?.Key;
      const singleV = payload?.settingValue ?? payload?.SettingValue ?? payload?.value ?? payload?.Value;
      if (singleK !== undefined && singleK !== null && String(singleK).trim() !== '') {
        out[String(singleK)] = singleV;
      }

      Object.entries(payload).forEach(([k, v]) => {
        if (!k) return;
        const nk = normalizeKey(k);
        if (['success', 'message', 'status', 'code', 'error', 'errors'].includes(nk)) return;
        if (k === 'data' || k === 'Data' || k === 'result' || k === 'Result') return;
        out[k] = v;
      });
    }

    return out;
  };

  const findSettingValue = (map, key, fallback = '') => {
    const keys = [key, ...(settingAliases[key] || [])];
    for (const k of keys) {
      const v = map[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    const wanted = new Set(keys.map(normalizeKey));
    for (const [k, v] of Object.entries(map || {})) {
      if (wanted.has(normalizeKey(k)) && v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
  };

  const comparable = (v) => String(v ?? '').trim().replace(/\s+/g, ' ');

  // ✅ FIX 1: Use Promise.allSettled so one failing endpoint doesn't crash the whole page
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [infoRes, settRes, usersRes] = await Promise.allSettled([
        api.get('/center-info'),
        api.get('/center-settings'),
        api.get('/center-users'),
      ]);

      if (infoRes.status === 'fulfilled' && isApiSuccess(infoRes.value.data)) {
        const d = extractPayload(infoRes.value.data);
        setInfo(d);
        setCenterForm({
          uniqueId: d.uniqueId, centerNameMT: d.centerNameMT, centerNameEn: d.centerNameEn,
          chillingCenter: d.chillingCenter, vehicleRoute: d.vehicleRoute, block: d.block,
          village: d.village, taluka: d.taluka, district: d.district, state: d.state,
          addressMT: d.addressMT, status: d.status || 'Active'
        });
        setBillingForm({ name: d.billingName, address: d.billingAddress, pincode: d.billingPincode, phone: d.billingPhone, email: d.billingEmail, gstin: d.billingGstin });
        setLocationForm({ latitude: d.latitude, longitude: d.longitude });
      }

      if (settRes.status === 'fulfilled' && isApiSuccess(settRes.value.data)) {
        setSettings(parseSettingsMap(settRes.value.data));
      }

      if (usersRes.status === 'fulfilled' && isApiSuccess(usersRes.value.data)) {
        const usersData = extractPayload(usersRes.value.data);
        setUsers(Array.isArray(usersData) ? usersData : []);
      }
    } catch (e) {
      console.warn('Center info load error:', e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ FIX 2: Use settingKey/settingValue (correct API body format)
  const saveSetting = async (key, value) => {
    const keysToTry = Array.from(new Set([key, ...(settingAliases[key] || [])]));
    let lastErr = 'Save failed.';
    let savedAny = false;

    for (const settingKey of keysToTry) {
      const reqVariants = [
        { method: 'post', body: { settingKey, settingValue: value } },
        { method: 'post', body: { key: settingKey, value } },
      ];
      let savedForThisKey = false;

      for (const variant of reqVariants) {
        try {
          const res = await api[variant.method]('/center-settings', variant.body);
          const ok = res?.data?.success !== false;
          if (ok) {
            setSettings(s => ({ ...s, [key]: value, [settingKey]: value }));
            savedAny = true;
            savedForThisKey = true;
            break;
          }
          lastErr = res.data?.message || `Save failed for ${settingKey}.`;
        } catch (e) {
          lastErr = e.response?.data?.message || e.message || `Error saving ${settingKey}.`;
        }
      }
      if (!savedForThisKey) {
        // continue trying alias keys even if one key format fails
      }
    }

    if (savedAny) {
      let verifiedMatch = true;
      let serverReturned = '';
      try {
        const verifyRes = await api.get('/center-settings');
        const map = parseSettingsMap(verifyRes.data);
        const actual = findSettingValue(map, key, '');
        serverReturned = String(actual ?? '');
        if (comparable(actual) !== comparable(value)) {
          verifiedMatch = false;
        }
      } catch {
        // If verification call fails, still refresh local view and report success attempt.
      }

      await fetchAll();
      closeAll();
      if (verifiedMatch) {
        showMsg('Saved.');
      } else {
        showMsg(
          `Saved request sent, but server still returns old value (${serverReturned || 'empty'}). Backend update logic needs fix for this setting.`,
          true
        );
      }
      return;
    }

    showMsg(`Error saving: ${lastErr}`, true);
  };

  const saveCenterForm = async () => {
    try {
      const res = await api.post('/center-info', centerForm);
      if (res.data?.success) { showMsg('Center details saved.'); closeDlg('center'); fetchAll(); }
      else showMsg(res.data?.message || 'Save failed.', true);
    } catch (e) { showMsg('Error saving: ' + (e.response?.data?.message || e.message), true); }
  };

  const saveBillingForm = async () => {
    try {
      const res = await api.post('/center-billing', billingForm);
      if (res.data?.success) { showMsg('Billing saved.'); closeDlg('billing'); fetchAll(); }
      else showMsg(res.data?.message || 'Save failed.', true);
    } catch (e) { showMsg('Error saving: ' + (e.response?.data?.message || e.message), true); }
  };

  const saveLocation = async () => {
    try {
      const res = await api.post('/center-location', locationForm);
      if (res.data?.success) { showMsg('Location saved.'); closeDlg('location'); fetchAll(); }
      else showMsg(res.data?.message || 'Save failed.', true);
    } catch (e) { showMsg('Error saving: ' + (e.response?.data?.message || e.message), true); }
  };

  const addUser = async () => {
    try {
      const res = await api.post('/center-users', userForm);
      if (res.data?.success) { showMsg('User added.'); closeDlg('addUser'); fetchAll(); }
      else showMsg(res.data?.message || 'Failed.', true);
    } catch (e) { showMsg('Error: ' + (e.response?.data?.message || e.message), true); }
  };

  const saveShiftTime = (dlgKey, valueKey) => {
    const d = getShiftDraft(dlgKey, valueKey);
    const formatted = `${d.startHour}:${d.startMinute} ${d.startMeridiem} - ${d.endHour}:${d.endMinute} ${d.endMeridiem}`;
    saveSetting(valueKey, formatted);
  };

  const saveShiftWiseRate = () => {
    const morning = settings.shiftWiseRateMorning || '+/- 0.0';
    const evening = settings.shiftWiseRateEvening || '+/- 0.0';
    const linked = settings.shiftWiseRateRequireMorning ? 'Linked to morning collection' : 'Independent';
    saveSetting('shiftWiseRate', `Morning: ${morning}, Evening: ${evening}, ${linked}`);
  };

  const saveContactDetails = () => {
    const name = (settings.contactName || '').trim();
    const phone = (settings.contactPhone || '').trim();
    const formatted = [name, phone].filter(Boolean).join(' | ');
    saveSetting('contactDetails', formatted);
  };

  const saveCenterLogo = () => {
    if (!logoFile) {
      showMsg('Please select logo image first.', true);
      return;
    }
    // Until dedicated upload endpoint is wired, persist selected file name as setting.
    saveSetting('centerLogo', logoFile.name);
    setLogoFile(null);
  };

  if (loading) return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: themeColor }} />
      </Box>
    </Layout>
  );

  const tabLabels = ['Collection Settings', 'SMS Settings', 'Invoice Settings', 'Rate Chart Settings', 'Farmer App Settings', 'Milk Sale Settings', 'Other Settings'];

  const settingsCols = [
    [
      [
        { label: 'Collection Type', key: 'collectionType', dlg: 'collType' },
        { label: 'Collection Shift', key: 'collectionShift', dlg: 'collShift' },
        { label: 'Collection Input', key: 'collectionInput', dlg: 'collInput' },
        { label: 'Morning Shift Time', key: 'morningShiftTime', dlg: 'morningTime', fallback: 'Not Available' },
        { label: 'Collection Print Setting', key: 'collPrintConfig', dlg: 'collPrint', fallback: 'Not Set' },
      ],
      [
        { label: 'Milk Type', key: 'milkType', dlg: null },
        { label: 'Show Previous Collection', key: 'showPrevCollection', dlg: null, fallback: 'Off' },
        { label: 'Offline Collection', key: 'offlineCollection', dlg: null, fallback: 'On' },
        { label: 'Evening Shift Time', key: 'eveningShiftTime', dlg: 'eveningTime', fallback: 'Not Available' },
      ],
    ],
    [
      [
        { label: 'SMS Type', key: 'smsType', dlg: 'smsType' },
        { label: 'Farmer App Link In SMS', key: 'farmerAppLink', dlg: 'farmerAppLink' },
        { label: 'Center Name In SMS', key: 'centerNameSms', dlg: 'centerNameSms' },
      ],
      [
        { label: 'Send SMS For', key: 'sendSmsFor', dlg: 'sendSmsFor' },
        { label: 'Total In Collection SMS', key: 'totalCollSms', dlg: 'totalCollSms' },
      ],
    ],
    [
      [
        { label: 'Payment Period', key: 'paymentPeriod', dlg: 'payPeriod' },
        { label: 'Invoice Print Setting', key: 'invPrintConfig', dlg: 'invPrint', fallback: 'Not Set' },
      ],
      [
        { label: 'Payment Register Print Setting', key: 'payRegConfig', dlg: 'payRegPrint', fallback: 'Not Set' },
      ],
    ],
    [
      [
        { label: 'Rate Chart', key: 'rateChart', dlg: 'rateChart' },
        { label: 'Farmer Wise Rate', key: 'farmerWiseRate', dlg: 'farmerWiseRate' },
      ],
      [
        { label: 'Shift Wise Rate', key: 'shiftWiseRate', dlg: 'shiftWiseRate' },
      ],
    ],
    [
      [
        { label: 'Show Advance/Loan Interest Rate', key: 'showAdvLoan', dlg: 'showAdvLoan', fallback: 'Off' },
        { label: 'Show Collection After Invoice', key: 'showCollAfterInv', dlg: 'showCollAfterInv', fallback: 'On' },
        { label: 'Hide Collection Rate', key: 'hideCollRate', dlg: 'hideCollRate', fallback: 'Off' },
      ],
      [
        { label: 'Show Rate Chart', key: 'showRateChart', dlg: 'showRateChart', fallback: 'Off' },
        { label: 'Show Collection After Shift', key: 'showCollShift', dlg: 'showCollShift', fallback: 'Off' },
      ],
    ],
    [
      [{ label: 'Billing Period', key: 'billPeriod', dlg: 'billPeriod' }],
      [{ label: 'Milk Sale Bill Print Setting', key: 'milkSaleConfig', dlg: 'milkSalePrint', fallback: 'Not Set' }],
    ],
    [
      [
        { label: 'Bank Details', key: 'bankDetails', dlg: 'bankDetails' },
        { label: 'CLR/Lacto Format', key: 'clrFormat', dlg: 'clrFormat' },
        { label: 'FAT Format', key: 'fatFormat', dlg: 'fatFormat' },
        { label: 'Computer Login', key: 'computerLogin', dlg: 'computerLogin' },
        { label: 'Annual Bonus Print Setting', key: 'annualBonusConfig', dlg: 'annualBonus', fallback: 'Not Set' },
      ],
      [
        { label: 'Weighing Scale Format', key: 'weighScale', dlg: 'weighScale' },
        { label: 'Contact Details', key: 'contactDetails', dlg: 'contactDetails' },
        { label: 'SNF Format', key: 'snfFormat', dlg: 'snfFormat' },
        { label: 'Center Logo', key: 'centerLogo', dlg: 'centerLogo' },
      ],
    ],
  ];

  const [leftCol, rightCol] = settingsCols[settingsTab];

  return (
    <Layout>
      <Box sx={{ px: 2, py: 1.5 }}>

        {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Typography sx={{ fontSize: 14, color: '#666', mb: 1.5 }}>
          Center Information &gt;&gt; {info?.centerNameEn || ''}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <SectionCard title="Center" themeColor={themeColor}
              actions={<EditBtn onClick={() => openDlg('center')} themeColor={themeColor} />}>
              <InfoRow label="Unique Id" value={info?.uniqueId} />
              <InfoRow label="Center Name (mother tongue)" value={info?.centerNameMT} />
              <InfoRow label="Center Name (English)" value={info?.centerNameEn} />
              <InfoRow label="Address (mother tongue)" value={info?.addressMT} />
              <InfoRow label="Address (English)" value={info?.addressEn} />
              <InfoRow label="Chilling Center" value={info?.chillingCenter} />
              <InfoRow label="Vehicle Route" value={info?.vehicleRoute} />
              <InfoRow label="Block" value={info?.block} />
            </SectionCard>

            <SectionCard title="Billing Info" themeColor={themeColor}
              actions={<EditBtn onClick={() => openDlg('billing')} themeColor={themeColor} />}>
              <Typography sx={{ fontSize: 13, color: '#888', mb: 1 }}>
                After any purchase from Mobile Dairy, the payment receipt will be created with the below mentioned details
              </Typography>
              <InfoRow label="Name" value={info?.billingName} />
              <InfoRow label="Address" value={info?.billingAddress} />
              <InfoRow label="Pincode" value={info?.billingPincode} />
              <InfoRow label="Phone Number" value={info?.billingPhone} />
              <InfoRow label="Email" value={info?.billingEmail} />
              <InfoRow label="GSTIN" value={info?.billingGstin} />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={5}>
            <SectionCard title="Subscription" themeColor={themeColor}
              actions={<>
                <Button size="small" startIcon={<Payment sx={{ fontSize: 13 }} />} onClick={() => openDlg('payment')}
                  sx={{ fontSize: 13, color: themeColor, textTransform: 'none', px: 0.8, py: 0.3 }}>Pay Now</Button>
                <Button size="small" startIcon={<History sx={{ fontSize: 13 }} />}
                  onClick={async () => {
                    try { const r = await api.get('/center-subscriptions'); if (r.data?.success) setSubs(r.data.data || []); } catch { }
                    openDlg('history');
                  }}
                  sx={{ fontSize: 13, color: themeColor, textTransform: 'none', px: 0.8, py: 0.3 }}>History</Button>
              </>}>
              <InfoRow label="Subscription" value={info?.subscriptionName} />
              <InfoRow label="Period" value={info?.subscriptionPeriod} />
              <InfoRow label="Status" value={info?.subscriptionStatus} />
            </SectionCard>

            <SectionCard title="Location" themeColor={themeColor}
              actions={<>
                <Button size="small" startIcon={<Map sx={{ fontSize: 13 }} />}
                  onClick={() => window.open(`https://maps.google.com/?q=${info?.latitude},${info?.longitude}`, '_blank')}
                  sx={{ fontSize: 13, color: themeColor, textTransform: 'none', px: 0.8, py: 0.3 }}>View On Map</Button>
                <EditBtn onClick={() => openDlg('location')} themeColor={themeColor} />
              </>}>
              <InfoRow label="Latitude" value={info?.latitude} />
              <InfoRow label="Longitude" value={info?.longitude} />
            </SectionCard>

            <SectionCard title="Deduction Type" themeColor={themeColor}>
              <InfoRow label="Other Payment" value={info?.deductOtherPayment || '—'} />
              <InfoRow label="Anamat" value={info?.deductAnamat || '—'} />
              <InfoRow label="Advance" value={info?.deductAdvance || '—'} />
              <InfoRow label="Food" value={info?.deductFood || '—'} />
              <Box sx={{ pt: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: '#333', fontWeight: 600 }}>Credit</Typography>
                <Typography sx={{ fontSize: 13, color: '#aaa', mt: 0.3 }}>Data not found</Typography>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── SETTINGS ──────────────────────────────────────── */}
        <Card sx={{ mb: 2, borderRadius: 1.5, boxShadow: '0 1px 4px #0001', border: '1px solid #e8e8e8' }}>
          <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: themeColor, mb: 1 }}>Settings</Typography>
            <Tabs value={settingsTab} onChange={(_, v) => setSettingsTab(v)}
              variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontSize: 13, textTransform: 'none', minHeight: 36, py: 0.5, minWidth: 'auto', px: 1.5 },
                '& .Mui-selected': { color: `${themeColor} !important`, fontWeight: 700, background: `${themeColor}12`, borderRadius: '4px 4px 0 0' },
                '& .MuiTabs-indicator': { background: themeColor },
              }}>
              {tabLabels.map((t, i) => <Tab key={i} label={t} />)}
            </Tabs>
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Grid container spacing={0}>
              <Grid item xs={6} sx={{ pr: { md: 2 } }}>
                {leftCol.map(row => (
                  <SettingRow key={row.key} label={row.label}
                    value={getSettingDisplayValue(row.key, row.fallback || '')}
                    onEdit={row.dlg ? () => openDlg(row.dlg) : () => { }} />
                ))}
              </Grid>
              <Grid item xs={6} sx={{ borderLeft: { md: '1px solid #f0f0f0' }, pl: { md: 2 } }}>
                {rightCol.map(row => (
                  <SettingRow key={row.key} label={row.label}
                    value={getSettingDisplayValue(row.key, row.fallback || '')}
                    onEdit={row.dlg ? () => openDlg(row.dlg) : () => { }} />
                ))}
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* ── CENTER USERS ──────────────────────────────────── */}
        <SectionCard title="Center Users" themeColor={themeColor}
          actions={
            <Button size="small" startIcon={<Add sx={{ fontSize: 13 }} />}
              onClick={() => { setUserForm({ name: '', mobile: '', role: 'Operator' }); openDlg('addUser'); }}
              sx={{ fontSize: 13, color: themeColor, textTransform: 'none' }}>
              + Add User
            </Button>
          }>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f8f8f8' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 13, py: 1 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 13, py: 1 }}>Mobile</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 13, py: 1 }}>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: 13, py: 1 }}>{u.name}</TableCell>
                    <TableCell sx={{ fontSize: 13, py: 1 }}>{u.mobile}</TableCell>
                    <TableCell sx={{ py: 0.8 }}>
                      <Typography sx={{ fontSize: 13, color: u.role === 'Owner' ? '#e53935' : themeColor, fontWeight: 600 }}>
                        {u.role}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

      </Box>

      {/* ═══════════════ DIALOGS ═══════════════════════════════ */}

      {/* Edit Center Details */}
      <Dialog open={dlg.center} onClose={() => closeDlg('center')} maxWidth="md" fullWidth
        PaperProps={{ sx: { minWidth: 700 } }}>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Center Details
          <IconButton onClick={() => closeDlg('center')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 4 }}>
          <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Unique Id</Typography>
                <TextField fullWidth size="small" value={centerForm.uniqueId || ''} disabled
                  sx={{ '& .MuiOutlinedInput-root': { background: '#f0f0f0', fontSize: 14 } }} />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Center Name (mother tongue)</Typography>
                <TextField fullWidth size="small" value={centerForm.centerNameMT || ''}
                  onChange={e => setCenterForm(f => ({ ...f, centerNameMT: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Center Name (English)</Typography>
                <TextField fullWidth size="small" value={centerForm.centerNameEn || ''}
                  onChange={e => setCenterForm(f => ({ ...f, centerNameEn: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Chilling Center</Typography>
                <Select size="small" value={centerForm.chillingCenter || ''}
                  onChange={e => setCenterForm(f => ({ ...f, chillingCenter: e.target.value }))}
                  sx={{ width: '100%', fontSize: 14 }}>
                  <MenuItem value="">None</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Vehicle Route</Typography>
                <Select size="small" value={centerForm.vehicleRoute || ''}
                  onChange={e => setCenterForm(f => ({ ...f, vehicleRoute: e.target.value }))}
                  sx={{ width: '100%', fontSize: 14 }}>
                  <MenuItem value="">None</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Block</Typography>
                <Select size="small" value={centerForm.block || ''}
                  onChange={e => setCenterForm(f => ({ ...f, block: e.target.value }))}
                  sx={{ width: '100%', fontSize: 14 }}>
                  <MenuItem value="">None</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Status</Typography>
                <Select size="small" value={centerForm.status || 'Active'}
                  onChange={e => setCenterForm(f => ({ ...f, status: e.target.value }))}
                  sx={{ width: '100%', fontSize: 14 }}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Village</Typography>
                <TextField fullWidth size="small" value={centerForm.village || ''}
                  onChange={e => setCenterForm(f => ({ ...f, village: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Taluka</Typography>
                <TextField fullWidth size="small" value={centerForm.taluka || ''}
                  onChange={e => setCenterForm(f => ({ ...f, taluka: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>District</Typography>
                <TextField fullWidth size="small" value={centerForm.district || ''}
                  onChange={e => setCenterForm(f => ({ ...f, district: e.target.value }))}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>State</Typography>
                <Select size="small" value={centerForm.state || ''}
                  onChange={e => setCenterForm(f => ({ ...f, state: e.target.value }))}
                  sx={{ width: '100%', fontSize: 14 }}>
                  {['Maharashtra','Karnataka','Gujarat','Goa','Telangana','Andhra Pradesh','Tamil Nadu','Kerala'].map(s =>
                    <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </Box>
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>Address (mother tongue)</Typography>
                <TextField fullWidth size="small" value={centerForm.addressMT || ''}
                  onChange={e => setCenterForm(f => ({ ...f, addressMT: e.target.value }))}
                  multiline rows={4} sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 2.5 }}>
          <Button onClick={() => closeDlg('center')} variant="outlined" size="small" sx={{ minWidth: 100, fontSize: 13 }}>CANCEL</Button>
          <Button onClick={saveCenterForm} variant="contained" size="small"
            sx={{ background: themeColor, '&:hover': { background: themeColor }, minWidth: 100, fontSize: 13 }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      {/* Billing Info */}
      <Dialog open={dlg.billing} onClose={() => closeDlg('billing')} maxWidth="md" fullWidth
        PaperProps={{ sx: { minWidth: 700 } }}>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Billing Info Edit
          <IconButton onClick={() => closeDlg('billing')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 4 }}>
          <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {[['name', 'Name'], ['address', 'Address'], ['pincode', 'Pincode']].map(([k, l]) => (
                <Box key={k} sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>{l}</Typography>
                  <TextField fullWidth size="small" value={billingForm[k] || ''}
                    onChange={e => setBillingForm(b => ({ ...b, [k]: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {[['phone', 'Phone Number'], ['email', 'Email'], ['gstin', 'GSTIN']].map(([k, l]) => (
                <Box key={k} sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 13, color: '#555', mb: 0.6, fontWeight: 500 }}>{l}</Typography>
                  <TextField fullWidth size="small" value={billingForm[k] || ''}
                    onChange={e => setBillingForm(b => ({ ...b, [k]: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 14 } }} />
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 2.5 }}>
          <Button onClick={() => closeDlg('billing')} variant="outlined" size="small" sx={{ minWidth: 100, fontSize: 13 }}>CANCEL</Button>
          <Button onClick={saveBillingForm} variant="contained" size="small"
            sx={{ background: themeColor, '&:hover': { background: themeColor }, minWidth: 100, fontSize: 13 }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      {/* Update Location */}
      <Dialog open={dlg.location} onClose={() => closeDlg('location')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Update Location
          <IconButton onClick={() => closeDlg('location')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: '#444', mb: 0.5 }}>Latitude</Typography>
            <TextField fullWidth size="small" value={locationForm.latitude || ''}
              onChange={e => setLocationForm(l => ({ ...l, latitude: e.target.value }))}
              InputProps={{ sx: { fontSize: 13.5 } }} />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, color: '#444', mb: 0.5 }}>Longitude</Typography>
            <TextField fullWidth size="small" value={locationForm.longitude || ''}
              onChange={e => setLocationForm(l => ({ ...l, longitude: e.target.value }))}
              InputProps={{ sx: { fontSize: 13.5 } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => closeDlg('location')} variant="outlined" size="small" sx={{ minWidth: 90, fontSize: 13 }}>CANCEL</Button>
          <Button onClick={saveLocation} variant="contained" size="small"
            sx={{ background: themeColor, '&:hover': { background: themeColor }, minWidth: 90, fontSize: 13 }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Payment */}
      <Dialog open={dlg.payment} onClose={() => closeDlg('payment')} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Payment
          <IconButton onClick={() => closeDlg('payment')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#3a3a8c', mb: 2 }}>Select Subscription</Typography>
          {[
            { name: 'Standard Plan - One Year - 2500', price: '₹ 2,500.00', features: ['One year subscription with basic features'] },
            { name: 'Advance Plan - One Year - 3200', price: '₹ 3,200.00', features: ['Standard Plan all features', 'Multiple Ratechart', '200 Active Farmers', 'Hardware Automation'] },
          ].map((plan, i) => (
            <Box key={i} sx={{ border: '1px solid #ddd', borderRadius: 2, p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox size="small" />
                  <Typography sx={{ fontWeight: 700, color: '#3a3a8c', fontSize: 13 }}>{plan.name}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, color: '#3a3a8c' }}>{plan.price}</Typography>
              </Box>
              {plan.features.map((f, j) => <Typography key={j} sx={{ fontSize: 13, color: '#555' }}>• {f}</Typography>)}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="contained" size="small" sx={{ background: themeColor }}>Pay Now</Button>
        </DialogActions>
      </Dialog>

      {/* History */}
      <Dialog open={dlg.history} onClose={() => closeDlg('history')} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Subscription History
          <IconButton onClick={() => closeDlg('history')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f5f5f5' }}>
                  {['Subscription', 'From Date', 'To Date', 'Transaction Date'].map(h =>
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 13 }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {subs.length === 0
                  ? <TableRow><TableCell colSpan={4} align="center" sx={{ color: '#aaa', fontSize: 13 }}>No history</TableCell></TableRow>
                  : subs.map((s, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 13 }}>{s.name}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{s.fromDate}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{s.toDate}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{s.transactionDate}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* Add User */}
      <Dialog open={dlg.addUser} onClose={() => closeDlg('addUser')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add User
          <IconButton onClick={() => closeDlg('addUser')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: '#444', mb: 0.5 }}>Name *</Typography>
            <TextField fullWidth size="small" value={userForm.name}
              onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))}
              InputProps={{ sx: { fontSize: 13.5 } }} />
          </Box>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 13, color: '#444', mb: 0.5 }}>Mobile</Typography>
            <TextField fullWidth size="small" value={userForm.mobile}
              onChange={e => setUserForm(f => ({ ...f, mobile: e.target.value }))}
              InputProps={{ sx: { fontSize: 13.5 } }} />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, color: '#444', mb: 0.5 }}>Role</Typography>
            <Select fullWidth size="small" value={userForm.role}
              onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}
              sx={{ fontSize: 13.5 }}>
              {['Owner', 'Operator', 'Manager', 'Accountant'].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => closeDlg('addUser')} variant="outlined" size="small" sx={{ minWidth: 90, fontSize: 13 }}>CANCEL</Button>
          <Button onClick={addUser} variant="contained" size="small"
            sx={{ background: themeColor, '&:hover': { background: themeColor }, minWidth: 90, fontSize: 13 }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      {/* ── SETTINGS DIALOGS ─────────────────────────────── */}

      <Dialog open={dlg.collType} onClose={() => closeDlg('collType')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Collection Type <IconButton onClick={() => closeDlg('collType')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.collectionType || 'FAT + SNF'}
            onChange={e => setSettings(s => ({ ...s, collectionType: e.target.value }))}>
            {['FAT only', 'CLR only', 'FAT + SNF', 'FAT + CLR', 'FAT + CLR + Auto SNF', 'Liter Only'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('collType')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('collectionType', settings.collectionType)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.collShift} onClose={() => closeDlg('collShift')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Collection Shift <IconButton onClick={() => closeDlg('collShift')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.collectionShift || 'Morning + Evening'}
            onChange={e => setSettings(s => ({ ...s, collectionShift: e.target.value }))}>
            {['Morning', 'Evening', 'Morning + Evening', 'Auto (Morning + Evening)'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('collShift')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('collectionShift', settings.collectionShift)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.collInput} onClose={() => closeDlg('collInput')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Collection Input <IconButton onClick={() => closeDlg('collInput')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.collectionInput || 'Manual + Automatic'}
            onChange={e => setSettings(s => ({ ...s, collectionInput: e.target.value }))}>
            {['Manual + Automatic', 'Weighing Scale + Milk Analyser Automatic', 'Only Milk Analyser Automatic', 'Only Milk Weighing Scale Automatic'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>Default Auto + Manual & set Auto for automatic entries</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('collInput')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('collectionInput', settings.collectionInput)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      {['morningTime', 'eveningTime'].map(key => (
        <Dialog key={key} open={dlg[key]} onClose={() => closeDlg(key)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {key === 'morningTime' ? 'Morning' : 'Evening'} Shift Time
            <IconButton onClick={() => closeDlg(key)} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {(() => {
              const valueKey = key === 'morningTime' ? 'morningShiftTime' : 'eveningShiftTime';
              const draft = getShiftDraft(key, valueKey);
              return (
                <>
                  {['Start Time', 'End Time'].map(label => {
                    const isStart = label === 'Start Time';
                    const hourField = isStart ? 'startHour' : 'endHour';
                    const minuteField = isStart ? 'startMinute' : 'endMinute';
                    const meridiemField = isStart ? 'startMeridiem' : 'endMeridiem';
                    return (
                      <Box key={label} sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 13, mb: 0.5 }}>{label}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            value={draft[hourField]}
                            onChange={e => setShiftDraft(key, cur => ({ ...cur, [hourField]: e.target.value.replace(/\D/g, '').slice(0, 2).padStart(2, '0') }))}
                            sx={{ width: 70 }}
                          />
                          <TextField
                            size="small"
                            value={draft[minuteField]}
                            onChange={e => setShiftDraft(key, cur => ({ ...cur, [minuteField]: e.target.value.replace(/\D/g, '').slice(0, 2).padStart(2, '0') }))}
                            sx={{ width: 70 }}
                          />
                          <FormControl size="small" sx={{ width: 80 }}>
                            <Select
                              value={draft[meridiemField]}
                              onChange={e => setShiftDraft(key, cur => ({ ...cur, [meridiemField]: e.target.value }))}
                            >
                              <MenuItem value="AM">AM</MenuItem>
                              <MenuItem value="PM">PM</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    );
                  })}
                </>
              );
            })()}
            <Typography sx={{ color: 'red', fontSize: 13 }}>Collections can only be added at the given shift timing</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={() => closeDlg(key)} variant="outlined" size="small">CANCEL</Button>
            <Button
              onClick={() => saveShiftTime(key, key === 'morningTime' ? 'morningShiftTime' : 'eveningShiftTime')}
              variant="contained"
              size="small"
              sx={{ background: themeColor }}
            >
              SAVE
            </Button>
          </DialogActions>
        </Dialog>
      ))}

      <Dialog open={dlg.smsType} onClose={() => closeDlg('smsType')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          SMS Type <IconButton onClick={() => closeDlg('smsType')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormGroup>
            {['Farmer App', 'SIM Card', 'Server SMS Pack'].map(o =>
              <FormControlLabel
                key={o}
                control={
                  <Checkbox
                    size="small"
                    checked={parseSettingList(settings.smsType, ['SIM Card']).includes(o)}
                    onChange={() => toggleListSetting('smsType', o, ['SIM Card'])}
                  />
                }
                label={o}
              />
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('smsType')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('smsType', settings.smsType ?? 'SIM Card')} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.sendSmsFor} onClose={() => closeDlg('sendSmsFor')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Send SMS For <IconButton onClick={() => closeDlg('sendSmsFor')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FormGroup>
            {['Collection', 'Food', 'Invoice Payment', 'Loan', 'Loan Installment', 'Milk Sale', 'Milk Receive', 'Milk Dispatch'].map(o =>
              <FormControlLabel
                key={o}
                control={
                  <Checkbox
                    size="small"
                    checked={parseSettingList(settings.sendSmsFor, ['Collection']).includes(o)}
                    onChange={() => toggleListSetting('sendSmsFor', o, ['Collection'])}
                  />
                }
                label={o}
              />
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('sendSmsFor')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('sendSmsFor', settings.sendSmsFor ?? 'Collection')} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.centerNameSms} onClose={() => closeDlg('centerNameSms')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Center Name In SMS <IconButton onClick={() => closeDlg('centerNameSms')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField fullWidth size="small" label="Name *" value={settings.centerNameSms || ''}
            onChange={e => setSettings(s => ({ ...s, centerNameSms: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('centerNameSms')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('centerNameSms', settings.centerNameSms)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <OnOffDialog open={dlg.farmerAppLink} title="Farmer App Link In SMS" value={settings.farmerAppLink || 'On'}
        hint="If 'On' Farmer application install link will be included in all SMS"
        onClose={() => closeDlg('farmerAppLink')} onSave={v => saveSetting('farmerAppLink', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.totalCollSms} title="Total In Collection SMS" value={settings.totalCollSms || 'On'}
        hint="If 'On' Milk collection SMS will included the total Liter and total Amount for invoice period"
        onClose={() => closeDlg('totalCollSms')} onSave={v => saveSetting('totalCollSms', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.rateChart} title="Rate Chart" value={settings.rateChart || 'On'}
        hint="Set 'Off' to stop rate chart temporarily"
        onClose={() => closeDlg('rateChart')} onSave={v => saveSetting('rateChart', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.bankDetails} title="Bank Details" value={settings.bankDetails || 'On'}
        hint="Set 'On' to display bank details"
        onClose={() => closeDlg('bankDetails')} onSave={v => saveSetting('bankDetails', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.computerLogin} title="Computer Login" value={settings.computerLogin || 'On'}
        hint="" onClose={() => closeDlg('computerLogin')} onSave={v => saveSetting('computerLogin', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.showAdvLoan} title="Show Advance/Loan Interest Rate" value={settings.showAdvLoan || 'Off'}
        hint="Set 'On' to display Advance/Loan interest rate in farmer application"
        onClose={() => closeDlg('showAdvLoan')} onSave={v => saveSetting('showAdvLoan', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.showRateChart} title="Show Rate Chart" value={settings.showRateChart || 'Off'}
        hint="Set 'On' to display Rate Chart in farmer application"
        onClose={() => closeDlg('showRateChart')} onSave={v => saveSetting('showRateChart', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.showCollAfterInv} title="Show Collection After Invoice Save" value={settings.showCollAfterInv || 'On'}
        hint="Set 'On' to show the collection to farmer after saving the bill"
        onClose={() => closeDlg('showCollAfterInv')} onSave={v => saveSetting('showCollAfterInv', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.showCollShift} title="Show Collection After Shift Finish" value={settings.showCollShift || 'Off'}
        hint="" onClose={() => closeDlg('showCollShift')} onSave={v => saveSetting('showCollShift', v)} themeColor={themeColor} />
      <OnOffDialog open={dlg.hideCollRate} title="Hide Collection Rate" value={settings.hideCollRate || 'Off'}
        hint="Set 'On' to hide collection rate in farmer application"
        onClose={() => closeDlg('hideCollRate')} onSave={v => saveSetting('hideCollRate', v)} themeColor={themeColor} />

      {/* Payment Period */}
      <Dialog open={dlg.payPeriod} onClose={() => closeDlg('payPeriod')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Payment Period <IconButton onClick={() => closeDlg('payPeriod')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup
            value={getSettingValue('paymentPeriod', '01-10, 11-20, 21-ME')}
            onChange={e => setSettings(s => ({ ...s, paymentPeriod: e.target.value, payPeriod: e.target.value }))}
          >
            {['01-10, 11-20, 21-ME', '01-15, 16-ME', '01-ME', '01-05, 06-10, 11-15, 16-20, 21-25, 26-ME',
              '06-15, 16-25, 26-05', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('payPeriod')} variant="outlined" size="small">CANCEL</Button>
          <Button
            onClick={() => saveSetting('paymentPeriod', getSettingValue('paymentPeriod', '01-10, 11-20, 21-ME'))}
            variant="contained"
            size="small"
            sx={{ background: themeColor }}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>

      {/* Billing Period */}
      <Dialog open={dlg.billPeriod} onClose={() => closeDlg('billPeriod')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Billing Period <IconButton onClick={() => closeDlg('billPeriod')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup
            value={getSettingValue('billPeriod', '01-10, 11-20, 21-ME')}
            onChange={e => setSettings(s => ({ ...s, billPeriod: e.target.value, billingPeriod: e.target.value }))}
          >
            {['01-10, 11-20, 21-ME', '01-15, 16-ME', '01-ME', '01-05, 06-10, 11-15, 16-20, 21-25, 26-ME',
              '06-15, 16-25, 26-05', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('billPeriod')} variant="outlined" size="small">CANCEL</Button>
          <Button
            onClick={() => saveSetting('billPeriod', getSettingValue('billPeriod', '01-10, 11-20, 21-ME'))}
            variant="contained"
            size="small"
            sx={{ background: themeColor }}
          >
            SAVE
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.farmerWiseRate} onClose={() => closeDlg('farmerWiseRate')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Farmer Wise Rate <IconButton onClick={() => closeDlg('farmerWiseRate')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.farmerWiseRate || 'As per Rate Chart'}
            onChange={e => setSettings(s => ({ ...s, farmerWiseRate: e.target.value }))}>
            {['As per Rate Chart', 'Rate Wise', 'Fat Wise'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>Farmers milk rate can be set more or less by 'Rate Wise' or 'FAT Wise'</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('farmerWiseRate')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('farmerWiseRate', settings.farmerWiseRate)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.shiftWiseRate} onClose={() => closeDlg('shiftWiseRate')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Shift Wise Rate <IconButton onClick={() => closeDlg('shiftWiseRate')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>Morning:</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="+/- 0.0"
            value={settings.shiftWiseRateMorning || ''}
            onChange={e => setSettings(s => ({ ...s, shiftWiseRateMorning: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Typography sx={{ fontSize: 13, mb: 0.5 }}>Evening:</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="+/- 0.0"
            value={settings.shiftWiseRateEvening || ''}
            onChange={e => setSettings(s => ({ ...s, shiftWiseRateEvening: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(settings.shiftWiseRateRequireMorning)}
                onChange={e => setSettings(s => ({ ...s, shiftWiseRateRequireMorning: e.target.checked }))}
              />
            }
            label={<Typography sx={{ fontSize: 13 }}>Apply evening bonus only when there is a morning collection on same date</Typography>} />
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>The amount will be added or subtracted according to the selected shift</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('shiftWiseRate')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={saveShiftWiseRate} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.clrFormat} onClose={() => closeDlg('clrFormat')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          CLR/Lacto Format <IconButton onClick={() => closeDlg('clrFormat')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.clrFormat || 'As per analyser'}
            onChange={e => setSettings(s => ({ ...s, clrFormat: e.target.value }))}>
            {['As per analyser', 'One digit(00)', 'Step (00.5)'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>This option is applicable only if Auto/Online input from Milk Analyzer is in use</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('clrFormat')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('clrFormat', settings.clrFormat)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.fatFormat} onClose={() => closeDlg('fatFormat')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          FAT Format <IconButton onClick={() => closeDlg('fatFormat')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.fatFormat || 'Round Down'}
            onChange={e => setSettings(s => ({ ...s, fatFormat: e.target.value }))}>
            {['Round Down', 'Round Up', 'Round'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>Applicable only if Auto/Online input from Milk Analyzer is in use</Typography>
          <Typography sx={{ color: 'red', fontSize: 13 }}>Round Up: 5.59 → 5.6 | Round Down: 5.59 → 5.5</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('fatFormat')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('fatFormat', settings.fatFormat)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.snfFormat} onClose={() => closeDlg('snfFormat')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          SNF Format <IconButton onClick={() => closeDlg('snfFormat')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.snfFormat || 'Round Down'}
            onChange={e => setSettings(s => ({ ...s, snfFormat: e.target.value }))}>
            {['Round Down', 'Round Up', 'Round'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>Applicable only if Auto/Online input from Milk Analyzer is in use</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('snfFormat')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('snfFormat', settings.snfFormat)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.weighScale} onClose={() => closeDlg('weighScale')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Weighing Scale Format <IconButton onClick={() => closeDlg('weighScale')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <RadioGroup value={settings.weighScale || 'As per Weighing Scale'}
            onChange={e => setSettings(s => ({ ...s, weighScale: e.target.value }))}>
            {['As per Weighing Scale', 'Zero digit (00)', 'One digit (00.0)', 'Two digit (00.00)'].map(o =>
              <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={o} />)}
          </RadioGroup>
          <Typography sx={{ color: 'red', fontSize: 13, mt: 1 }}>Applicable only if Auto/Online input from weighing scale is in use</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('weighScale')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={() => saveSetting('weighScale', settings.weighScale)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.contactDetails} onClose={() => closeDlg('contactDetails')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Contact Details <IconButton onClick={() => closeDlg('contactDetails')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={settings.contactName || ''}
            onChange={e => setSettings(s => ({ ...s, contactName: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Phone Number"
            value={settings.contactPhone || ''}
            onChange={e => setSettings(s => ({ ...s, contactPhone: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Typography sx={{ color: 'red', fontSize: 13 }}>Contact Information to be printed on invoice. If blank, center owner info will be used.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('contactDetails')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={saveContactDetails} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dlg.centerLogo} onClose={() => closeDlg('centerLogo')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Center Logo <IconButton onClick={() => closeDlg('centerLogo')} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafafa', mb: 1 }}
            onClick={() => document.getElementById('logoInput').click()}>
            <Add sx={{ fontSize: 40, color: '#aaa' }} />
          </Box>
          <input id="logoInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setLogoFile(e.target.files?.[0] || null)} />
          {logoFile && <Typography sx={{ fontSize: 12, mb: 0.5, color: '#444' }}>Selected: {logoFile.name}</Typography>}
          <Typography sx={{ color: 'red', fontSize: 13 }}>This logo will be printed on bill print</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => closeDlg('centerLogo')} variant="outlined" size="small">CANCEL</Button>
          <Button onClick={saveCenterLogo} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
        </DialogActions>
      </Dialog>

      <CollectionPrintDialog open={dlg.collPrint} onClose={() => closeDlg('collPrint')} themeColor={themeColor}
        value={settings.collPrintConfig || {}} onSave={v => saveSetting('collPrintConfig', v)} />
      <InvoicePrintDialog open={dlg.invPrint} onClose={() => closeDlg('invPrint')} themeColor={themeColor}
        value={settings.invPrintConfig || {}} onSave={v => saveSetting('invPrintConfig', v)} />
      <PayRegPrintDialog open={dlg.payRegPrint} onClose={() => closeDlg('payRegPrint')} themeColor={themeColor}
        value={settings.payRegConfig || {}} onSave={v => saveSetting('payRegConfig', v)} />
      <MilkSalePrintDialog open={dlg.milkSalePrint} onClose={() => closeDlg('milkSalePrint')} themeColor={themeColor}
        value={settings.milkSaleConfig || {}} onSave={v => saveSetting('milkSaleConfig', v)} />
      <AnnualBonusDialog open={dlg.annualBonus} onClose={() => closeDlg('annualBonus')} themeColor={themeColor}
        value={settings.annualBonusConfig || {}} onSave={v => saveSetting('annualBonusConfig', v)} />

    </Layout>
  );
}

// ── Complex Print Dialogs ──────────────────────────────────────

function CollectionPrintDialog({ open, onClose, value, onSave, themeColor }) {
  const [cfg, setCfg] = useState(value);
  useEffect(() => { setCfg(value); }, [value, open]);
  const toggle = k => setCfg(c => ({ ...c, [k]: !c[k] }));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Collection Print Setting <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Print Language</InputLabel>
              <Select value={cfg.printLanguage || 'मराठी'} label="Print Language"
                onChange={e => setCfg(c => ({ ...c, printLanguage: e.target.value }))}>
                {['मराठी', 'हिंदी', 'English', 'ಕನ್ನಡ'].map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Receipt Printing</Typography>
            <Grid container>
              {['FAT', 'SNF', 'CLR', 'Liter', 'Rate', 'Amount', 'Total Liter', 'Total Amount', 'Center Logo', 'Number in language'].map(f => (
                <Grid item xs={6} key={f}>
                  <FormControlLabel control={<Checkbox size="small" checked={!!cfg[f]} onChange={() => toggle(f)} />}
                    label={<Typography sx={{ fontSize: 13 }}>{f}</Typography>} />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Note" placeholder="Enter Note" sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Print Format</Typography>
            <RadioGroup value={cfg.printFormat || 'Format-1'} onChange={e => setCfg(c => ({ ...c, printFormat: e.target.value }))}>
              {['Format-1', 'Format-2', 'Format-3'].map(f => <FormControlLabel key={f} value={f} control={<Radio size="small" />} label={f} />)}
            </RadioGroup>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">CANCEL</Button>
        <Button onClick={() => onSave(cfg)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
      </DialogActions>
    </Dialog>
  );
}

function InvoicePrintDialog({ open, onClose, value, onSave, themeColor }) {
  const [cfg, setCfg] = useState(value);
  useEffect(() => { setCfg(value); }, [value, open]);
  const selects = [
    ['printLanguage', 'Print Language', ['मराठी', 'हिंदी', 'English', 'ಕನ್ನಡ']],
    ['perPaper', 'Per Paper', ['Horizontal Two Bill', 'One Bill', 'Two Bill']],
    ['separateCowBuff', 'Separate Cow/Buffalo', ['On', 'Off']],
    ['numberInLang', 'Number in language', ['On', 'Off']],
    ['rateColumn', 'Rate Column', ['On', 'Off']],
    ['feedDetails', 'Feed Details', ['On', 'Off']],
    ['advanceDetails', 'Advance Details', ['On', 'Off']],
    ['averageInInvoice', 'Average in invoice', ['On', 'Off']],
    ['plantName', 'Plant Name', ['On', 'Off']],
    ['printer', 'Printer', ['Laser', 'Thermal']],
  ];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Invoice Print Setting <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {selects.slice(0, 5).map(([k, label, opts]) => (
              <FormControl key={k} fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select value={cfg[k] || opts[0]} label={label} onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {['FAT', 'SNF', 'CLR'].map(f =>
                <FormControlLabel key={f} control={<Checkbox size="small" checked={!!cfg[f]} onChange={() => setCfg(c => ({ ...c, [f]: !c[f] }))} />} label={f} />)}
            </Box>
          </Grid>
          <Grid item xs={6}>
            {selects.slice(5).map(([k, label, opts]) => (
              <FormControl key={k} fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select value={cfg[k] || opts[0]} label={label} onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <TextField fullWidth size="small" label="Note" placeholder="Enter Note" sx={{ mb: 2 }} />
            <RadioGroup value={cfg.printFormat || 'Format-3'} onChange={e => setCfg(c => ({ ...c, printFormat: e.target.value }))}>
              {['Format-1', 'Format-2', 'Format-3'].map(f => <FormControlLabel key={f} value={f} control={<Radio size="small" />} label={f} />)}
            </RadioGroup>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">CANCEL</Button>
        <Button onClick={() => onSave(cfg)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
      </DialogActions>
    </Dialog>
  );
}

function PayRegPrintDialog({ open, onClose, value, onSave, themeColor }) {
  const [cfg, setCfg] = useState(value);
  useEffect(() => { setCfg(value); }, [value, open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Payment Register Print Setting <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {[['signatureColumn', 'Signature Column'], ['zeroAmountColumn', 'Zero Amount Column']].map(([k, label]) => (
              <FormControl key={k} fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select value={cfg[k] || 'On'} label={label} onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}>
                  <MenuItem value="On">On</MenuItem><MenuItem value="Off">Off</MenuItem>
                </Select>
              </FormControl>
            ))}
          </Grid>
          <Grid item xs={6}>
            <RadioGroup value={cfg.printFormat || 'Format-1'} onChange={e => setCfg(c => ({ ...c, printFormat: e.target.value }))}>
              {['Format-1', 'Format-2'].map(f => <FormControlLabel key={f} value={f} control={<Radio size="small" />} label={f} />)}
            </RadioGroup>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">CANCEL</Button>
        <Button onClick={() => onSave(cfg)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
      </DialogActions>
    </Dialog>
  );
}

function MilkSalePrintDialog({ open, onClose, value, onSave, themeColor }) {
  const [cfg, setCfg] = useState(value);
  useEffect(() => { setCfg(value); }, [value, open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Milk Sale Bill Print Setting <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {[['printLanguage', 'Print Language', ['मराठी', 'हिंदी', 'English', 'ಕನ್ನಡ']],
              ['numberInLang', 'Number in language', ['On', 'Off']],
              ['printer', 'Printer', ['Laser', 'Thermal']]].map(([k, label, opts]) => (
              <FormControl key={k} fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select value={cfg[k] || opts[0]} label={label} onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
          </Grid>
          <Grid item xs={6}>
            <RadioGroup value={cfg.printFormat || 'Format-1'} onChange={e => setCfg(c => ({ ...c, printFormat: e.target.value }))}>
              {['Format-1', 'Format-2'].map(f => <FormControlLabel key={f} value={f} control={<Radio size="small" />} label={f} />)}
            </RadioGroup>
            <TextField fullWidth size="small" label="Note" placeholder="Enter Note" sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">CANCEL</Button>
        <Button onClick={() => onSave(cfg)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
      </DialogActions>
    </Dialog>
  );
}

function AnnualBonusDialog({ open, onClose, value, onSave, themeColor }) {
  const [cfg, setCfg] = useState(value);
  useEffect(() => { setCfg(value); }, [value, open]);
  const selects = [
    ['printLanguage', 'Print Language', ['English', 'मराठी', 'हिंदी']],
    ['perPaper', 'Per Paper', ['One Bill', 'Two Bill']],
    ['anamatDetails', 'Anamat Details', ['On', 'Off']],
    ['anamatDate', 'Anamat Date', ['On', 'Off']],
    ['anamatOpeningBalance', 'Anamat Opening Balance', ['On', 'Off']],
    ['anamatLiters', 'Anamat Liters', ['On', 'Off']],
    ['rebateDetails', 'Rebate Details', ['On', 'Off']],
    ['detailsOrderBy', 'Details Order By', ['Order By Date - Descending', 'Order By Date - Ascending']],
  ];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ background: themeColor, color: '#fff', py: 1.5, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Annual Bonus Print Setting <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            {selects.slice(0, 4).map(([k, label, opts]) => (
              <FormControl key={k} fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select value={cfg[k] || opts[0]} label={label} onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
          </Grid>
          <Grid item xs={6}>
            {selects.slice(4).map(([k, label, opts]) => (
              <FormControl key={k} fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>{label}</InputLabel>
                <Select value={cfg[k] || opts[0]} label={label} onChange={e => setCfg(c => ({ ...c, [k]: e.target.value }))}>
                  {opts.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <TextField fullWidth size="small" label="Bill Note" placeholder="Enter Greeting" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">CANCEL</Button>
        <Button onClick={() => onSave(cfg)} variant="contained" size="small" sx={{ background: themeColor }}>SAVE</Button>
      </DialogActions>
    </Dialog>
  );
}
